"use client";

import { useCallback, useMemo } from "react";
import {
  useGlobalPartners,
  useGlobalRoutes,
} from "@/query/hooks/useUnifiedCache";
import { useEmployees } from "@/query/hooks/useEmployees";
import {
  formatZoned,
  useCompanyTimezone,
} from "@/lib/datetime/companyTimezone";
import { useRecapLocationsOffline } from "./useRecapLocationsOffline";
import { snakeToCamel } from "../components/cells/AiChangeBadge";

/**
 * Shared value formatter for AI-suggested **header** field changes. Returns a
 * `(field, value) => string` that resolves the raw `ai_suggestions[]` value
 * into the human-readable label the user already sees in the header form:
 * partner / employee / location / route UUIDs → names, ISO dates → tz-aware
 * `yyyy-MM-dd`, everything else stringified.
 *
 * Single source of truth for both `AiHeaderChangesBanner` (the list-view
 * banner) and `AiHeaderFieldBadge` (the per-field inline diff on the focus
 * view) so the two surfaces never drift on label / value resolution.
 *
 * `field` may be snake_case (DB column) or camelCase (wire key) — normalized
 * internally so callers can pass whichever they hold.
 */
export function useHeaderAiFieldFormatter(): (
  field: string,
  value: unknown,
) => string {
  const tz = useCompanyTimezone();
  const { data: partners = [] } = useGlobalPartners();
  const { data: employees = [] } = useEmployees();
  const { data: locations = [] } = useRecapLocationsOffline();
  const { data: routes = [] } = useGlobalRoutes();

  const partnerNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of partners) {
      const name =
        (p as { usage_name?: string | null; name?: string | null })
          .usage_name ??
        (p as { name?: string | null }).name ??
        "";
      if (name) map.set(p.id, name);
    }
    return map;
  }, [partners]);

  const employeeNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of employees) {
      const parts = [e.name].filter(Boolean);
      if (e.code) parts.push(`(${e.code})`);
      const label = parts.join(" ").trim();
      if (label) map.set(e.id, label);
    }
    return map;
  }, [employees]);

  const locationNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const l of locations) {
      const label = l.code ? `${l.code} - ${l.name}` : l.name;
      if (label) map.set(l.id, label);
    }
    return map;
  }, [locations]);

  const routeNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of routes) {
      const label = r.code ? `${r.code} - ${r.name}` : r.name;
      if (label) map.set(r.id, label);
    }
    return map;
  }, [routes]);

  return useCallback(
    (field: string, value: unknown): string => {
      if (value === null || value === undefined || value === "") return "—";
      const f = snakeToCamel(field);
      if (typeof value === "string") {
        if (f === "partnerId") {
          return partnerNameById.get(value) ?? shortenId(value);
        }
        if (f === "employeeId") {
          return employeeNameById.get(value) ?? shortenId(value);
        }
        if (f === "locationId") {
          return locationNameById.get(value) ?? shortenId(value);
        }
        if (f === "routeId") {
          return routeNameById.get(value) ?? shortenId(value);
        }
        if (f === "shippingAddressId") {
          // No global shipping-address cache today: surface a short suffix
          // rather than a raw UUID so the diff stays readable.
          return shortenId(value);
        }
        if (f === "deliveryDate" || f === "orderDate") {
          return formatZoned(value, tz, "yyyy-MM-dd") || value;
        }
        return value;
      }
      if (typeof value === "number") {
        return Number.isInteger(value) ? String(value) : String(Number(value));
      }
      return JSON.stringify(value);
    },
    [partnerNameById, employeeNameById, locationNameById, routeNameById, tz],
  );
}

/**
 * Display fallback for an unresolved UUID: keep the last 4 chars so users
 * still get a stable, copy-pastable hint without a 36-char dump.
 */
function shortenId(id: string): string {
  return id.length > 4 ? `…${id.slice(-4)}` : id;
}
